import get from 'lodash/get';
import fromPairs from 'lodash/fromPairs';
import pickBy from 'lodash/pickBy';
import filter from 'lodash/filter';
import { addRawFormData } from 'common/entities/form';
import { ENTITIES_KEY_NODE, setNodeAvailability } from 'common/entities/formNode';
import { hasFormNodesResponseLimit, getAllFormNodeEntities, getFormNodes } from 'common/entities/formNode/selectors';
import { getFormRendererV3ApiClient } from 'lib/SplashApiClient/subclients/FormRendererV3ApiClient';
import { NODE_NAME_FIRST_NAME, NODE_NAME_LAST_NAME, NODE_NAME_EMAIL } from 'common/entities/formNode/nodeNames';
import { INPUT_TYPE_CHECKBOX } from 'common/entities/formNode/inputTypes';
import { NODE_TYPE_QUESTION } from 'common/entities/formNode/nodeTypes';
import { getUrlParams } from 'common/utils/getUrlParams';
import normalizeApiResponse from 'utils/normalizeApiResponse';
import { sessionsSchema } from 'common/schemas/session';
import Logger from 'observability/Logger';
import { sanitizeError } from 'utils/sanitizeError';

import {
    SET_FORM_ID,
    LOADED_FORM_DATA,
    SET_UPLOAD_URL,
    SET_SUBMIT_URL,
    SET_EVENT_ID,
    SET_FORM_INITIAL_STATE,
    SET_FORM_IS_UPDATING,
    SET_VIEWER_DATA,
    SET_GUEST_ACCESS_HASH,
    SET_RSVP_FORM_METADATA,
    SET_FORM_IS_MOBILE_RSVP,
    SET_LIMIT_ONE_ORDER_PER_EMAIL,
    SET_LIMIT_ONE_TICKET_TYPE_PER_EMAIL,
    SET_IS_ATTENDING,
    SET_IS_REGISTRATION_OPEN,
    SET_SESSIONS_STEP,
    SET_FORM_SESSIONS,
} from '../constants/actionTypes';
import { getEventId, getViewerData } from '../selectors';
import { setFormValues, setFormInitialValues, updateFormValue } from './form';

const LOG = new Logger('FormRenderer-actions');

export const setFormId = (formId) => ({
    type: SET_FORM_ID,
    payload: formId,
});

export const setUploadUrl = (uploadUrl) => ({
    type: SET_UPLOAD_URL,
    payload: uploadUrl,
});

export const setSubmitUrl = (submitUrl) => ({
    type: SET_SUBMIT_URL,
    payload: submitUrl,
});

export const setEventId = (eventId) => ({
    type: SET_EVENT_ID,
    payload: eventId,
});

// (String, Object, String) => Object
export const setFormInitialState = (formInitialState) => ({
    type: SET_FORM_INITIAL_STATE,
    payload: formInitialState,
});

export const setIsUpdating = (isUpdating) => ({
    type: SET_FORM_IS_UPDATING,
    payload: isUpdating,
});

export const setViewerData = (viewerData) => ({
    type: SET_VIEWER_DATA,
    payload: viewerData,
});

export const setGuestAccessHash = (guestAccessHash) => ({
    type: SET_GUEST_ACCESS_HASH,
    payload: guestAccessHash,
});

export const setRsvpFormMetadata = ({ formId, formName }) => ({
    type: SET_RSVP_FORM_METADATA,
    payload: {
        formId,
        formName,
    },
});

export const setIsMobileRsvp = (isMobileRsvp) => ({
    type: SET_FORM_IS_MOBILE_RSVP,
    payload: isMobileRsvp,
});

export const setLimitOneOrderPerEmail = (limitOneOrderPerEmail) => ({
    type: SET_LIMIT_ONE_ORDER_PER_EMAIL,
    payload: limitOneOrderPerEmail,
});

export const setLimitOneTicketTypePerEmail = (limitOneTicketTypePerEmail) => ({
    type: SET_LIMIT_ONE_TICKET_TYPE_PER_EMAIL,
    payload: limitOneTicketTypePerEmail,
});

export const setIsAttending = () => ({
    type: SET_IS_ATTENDING,
});

export const setIsRegistrationOpen = (isRegistrationOpen) => ({
    type: SET_IS_REGISTRATION_OPEN,
    payload: isRegistrationOpen,
});

export const loadResponseAvailability =
    () =>
    (dispatch, getState, { formRendererV3ApiClient = getFormRendererV3ApiClient() } = {}) => {
        const eventId = getEventId(getState());
        if (!eventId) {
            throw new Error('No eventId in formRenderer');
        }
        formRendererV3ApiClient
            .getRemainigResponses(eventId)
            .then((response) => {
                dispatch(setNodeAvailability(response));
            })
            .catch((error) => {
                LOG.error('Error on loadResponseAvailability', sanitizeError(error));
                throw error;
            });
    };

/**
 * @param {Object} normalizedFormData
 * @return {bool}
 */
export const doesFormHaveResponseLimits = (normalizedFormData) =>
    !!Object.values(normalizedFormData.entities[ENTITIES_KEY_NODE]).find(
        (node) =>
            node.config &&
            node.config.options &&
            !!Object.values(node.config.options.entities).find((option) => !!option.responseLimit),
    );

export const setFormData = (formData) => (dispatch, getState) => {
    dispatch(addRawFormData(formData));
    dispatch({
        type: LOADED_FORM_DATA,
        formId: formData.id,
    });

    if (hasFormNodesResponseLimit(getState(), `${formData.id}`)) {
        dispatch(loadResponseAvailability());
    }
};

// Adding new pairs to this object populates the form from viewer data.
const mappedNodes = {
    [NODE_NAME_FIRST_NAME]: 'first_name',
    [NODE_NAME_LAST_NAME]: 'last_name',
    [NODE_NAME_EMAIL]: 'email',
};

/**
 * Map node ids with viewer data values using an intermediate object of mappedNodes.
 * Example: node.name is first-name, so through mappedNodes it's changed to first_name,
 * which is the key in viewerData.
 * @param {Object} formNodes
 * @param {Object} viewerData
 * @return {Object}
 */
export const mapViewerData = (formNodes, viewerData) => {
    const initialValuesByMappedName = formNodes
        .filter((node) => viewerData[mappedNodes[node.name]] !== undefined)
        .reduce(
            (acc, node) => ({
                ...acc,
                [node.id]: viewerData[mappedNodes[node.name]],
            }),
            {},
        );

    const allNodeIds = formNodes.map((node) => node.id);
    const allNodeIdsMap = fromPairs(allNodeIds.map((id) => [id, true]));

    const initialValuesByNodeId = pickBy(
        viewerData.customNode || {},
        (value, nodeId) => allNodeIdsMap[nodeId] && value != null,
    );

    return {
        ...initialValuesByMappedName,
        ...initialValuesByNodeId,
    };
};

/**
 * @param {string} urlData
 * @return {boolean}
 */
const getCheckboxValueAsBoolean = (urlData) =>
    urlData && urlData !== '0' && urlData !== 'false' && urlData !== 'undefined' && urlData !== 'null';

/**
 * @param {string} urlData
 * @param {string} nodeInputType
 * @return {string|boolean}
 */
export const getUrlDataFromInputType = (urlData, nodeInputType) => {
    const NODE_TYPE_TO_VALUE = {
        [INPUT_TYPE_CHECKBOX]: (value) => getCheckboxValueAsBoolean(value),
    };

    return NODE_TYPE_TO_VALUE[nodeInputType] ? NODE_TYPE_TO_VALUE[nodeInputType](urlData) : urlData;
};

/**
 * Map node ids with the query params gathered from the url if they have a value.
 * @param {Object} formNodes
 * @param {Object} urlData
 * @return {Object}
 */

export const mapUrlData = (formNodes, urlData) =>
    formNodes.reduce(
        (acc, node) =>
            urlData[node.urlParamName]
                ? {
                      ...acc,
                      [node.id]: getUrlDataFromInputType(urlData[node.urlParamName], node.inputType),
                  }
                : acc,
        {},
    );

// This is the separator that Splash's back-end uses to separate multi select values.
const OPTION_SEPARATOR = '||';

/**
 * Map node ids with the default selected values they may have in case of radio/checkbox/selects.
 * @param {Object} formNodes
 * @return {Object}
 */
const getDefaultOptionsFromEntities = (formNodes) =>
    formNodes.reduce((acc, node) => {
        const optionEntities = get(node, 'config.options.entities');

        const selectedEntities = optionEntities ? Object.values(optionEntities).filter(({ selected }) => selected) : [];

        return selectedEntities.length
            ? {
                  ...acc,
                  [node.id]: selectedEntities.map((entity) => entity.value).join(OPTION_SEPARATOR),
              }
            : acc;
    }, {});

/**
 * Map node ids with the default checked values they may have in single checkbox questions
 * @param {Object} formNodes
 * @return {Object}
 */
const getDefaultConfigChecked = (formNodes) =>
    formNodes.reduce((acc, node) => {
        const checked = get(node, 'config.checked');

        return checked !== undefined
            ? {
                  ...acc,
                  [node.id]: checked,
              }
            : acc;
    }, {});

/**
 * Map node ids with the initial value set in Form Builder for hidden inputs.
 * @param {Object} formNodes
 * @return {Object}
 */
const getHiddenFieldsDefaultValues = (formNodes) =>
    formNodes.reduce((acc, node) => {
        const optionInitialValue = get(node, 'config.initialValue');

        return optionInitialValue
            ? {
                  ...acc,
                  [node.id]: optionInitialValue,
              }
            : acc;
    }, {});

/**
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * !!!! ANY CHANGES TO THIS METHOD WILL AFFECT EXISTING CUSTOM JS USING THIS !!!!
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * We use this method to transform the data provided to us by the setRegistrationFormValues method
 * From key value pair of { code: value } --> { nodeId: value }
 * @param {Object} state
 * @param {Object} values
 * @return {Object} transformed values
 */
export const transformCustomJsValues = (state, values) => {
    if (!values) return {};

    const formNodes = getAllFormNodeEntities(state);
    const formNodeQuestions = filter(formNodes, (node) => node.type === NODE_TYPE_QUESTION);

    const formNodeQuestionCodeToIdMap = formNodeQuestions.reduce(
        (acc, node) => ({
            ...acc,
            [node.code]: node.id,
        }),
        {},
    );

    return Object.entries(values).reduce((acc, value) => {
        const key = value[0];
        const nodeId = formNodeQuestionCodeToIdMap[key];
        const nodeValue = value[1];

        return nodeId ? { ...acc, [nodeId]: nodeValue } : acc;
    }, {});
};

/**
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * !!!! ANY CHANGES TO THIS METHOD WILL AFFECT EXISTING CUSTOM JS USING THIS !!!!
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * We use this method to set registration form values after the initial load
 * @param {Object} formValues
 */
export const setCustomFormValues = (formValues) => (dispatch, getState) => {
    const state = getState();
    const transformedValues = transformCustomJsValues(state, formValues);
    Object.entries(transformedValues).forEach((transformedValue) => {
        const [nodeId, value] = transformedValue;
        dispatch(updateFormValue(nodeId, value));
    });
};

/**
 * Populate form fields using form id and data stored in the store.
 * @param {Object} form
 */
export const populateFormFields = (form, group) => (dispatch, getState) => {
    const state = getState();
    const formId = form && form.id;

    const viewerData = getViewerData(state);
    const urlData = getUrlParams();
    const formNodes = getFormNodes(state, `${formId}`);

    const mappedViewerData = viewerData && mapViewerData(formNodes, viewerData);
    const mappedUrlData = urlData && mapUrlData(formNodes, urlData);
    const mappedDefaultOptionsEntities = getDefaultOptionsFromEntities(formNodes);
    const mappedHiddenFields = getHiddenFieldsDefaultValues(formNodes);
    const mappedDefaultConfigChecked = getDefaultConfigChecked(formNodes);

    const customJsValues =
        typeof window.setRegistrationFormValues === 'function' ? window.setRegistrationFormValues() : {};

    const customJsInitialValues = transformCustomJsValues(state, customJsValues);

    const formValues = {
        ...mappedDefaultConfigChecked,
        ...mappedHiddenFields,
        ...mappedDefaultOptionsEntities,
        ...mappedUrlData,
        ...mappedViewerData,
        // The custom JS values should be last in order to set the values
        // ANY CHANGES TO THIS WILL AFFECT EXISTING CUSTOM JS USING THIS LOGIC
        ...customJsInitialValues,
    };

    dispatch(setFormInitialValues(formValues, group));
    dispatch(setFormValues(formValues, group));
};

export const setSessionsStep = (sessionsStep) => ({
    type: SET_SESSIONS_STEP,
    payload: sessionsStep,
});

export const setFormSessions = (response) => ({
    type: SET_FORM_SESSIONS,
    payload: normalizeApiResponse(response, sessionsSchema),
});
